// src/components/business/BusinessDemandsTab.tsx
//
// Aba "Demandas" da tela do negócio — wireframe seção 04 (#bp-dem): subtítulo
// "Demandas deste negócio — reusa os cards do Dashboard" + lista reusando
// DemandRichCard (ações Aprovar/Reprovar embutidas) e DemandDetailPanel.
//
// C4/C6: LAZY — busca api.listDemands({ business_id }) quando a aba abre
// (filtro server-side novo, W1.3). Fallback defensivo: backend antigo
// (pré-restart) IGNORA o param e responde sem filtro → filtramos client-side
// pela coluna business_id presente na row.
//
// DEM-2162 — quando o business é COMPARTILHADO via colaboração, a API retorna
// demandas de TODOS os donos (regra de visibilidade por colaboração) + o campo
// owner_tenant {id,name} em cada demanda. Esta aba:
//   - constrói DINAMICAMENTE um seletor de dono a partir dos owners distintos
//     retornados ("Todas" / "Minhas" / "Do {nome}" por colaborador) — genérico
//     pra N colaboradores, NUNCA hardcoded em nomes;
//   - exibe um BADGE do tenant dono em cada card (DemandRichCard.ownerLabel).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type Demand } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { DemandRichCard } from '@/pages/mc/DemandRichCard';
import { DemandDetailPanel } from '@/pages/mc/DemandDetailPanel';
import { WfEmpty } from '@/components/wf';
import { ClipboardList } from 'lucide-react';
import { filterDemandsByBusiness } from './business-constants';

interface BusinessDemandsTabProps {
  businessId: string;
}

// Rótulo do dono resolvido do owner_tenant (fallback pro id curto se sem nome).
function ownerLabelFor(demand: Demand): string | null {
  const ot = demand.owner_tenant;
  if (!ot) return null;
  return ot.name ?? `${ot.id.slice(0, 8)}…`;
}

export function BusinessDemandsTab({ businessId }: BusinessDemandsTabProps) {
  const { tenant } = useAuth();
  const myTenantId = tenant?.id ?? null;
  const [demands, setDemands] = useState<Demand[] | null>(null);
  const [error, setError] = useState('');
  const [detailDemand, setDetailDemand] = useState<Demand | null>(null);
  // DEM-2162 — filtro de dono selecionado: 'all' | 'mine' | '<tenant_id>'.
  const [ownerFilter, setOwnerFilter] = useState<string>('all');

  const load = useCallback(() => {
    api.listDemands({ business_id: businessId, per_page: 200 })
      .then((res) => {
        const items = (res.items ?? res.demands ?? []) as Demand[];
        setDemands(filterDemandsByBusiness(items, businessId));
        setError('');
      })
      .catch((err) => {
        setDemands((prev) => prev ?? []);
        setError(err instanceof Error ? err.message : 'Erro ao carregar demandas');
      });
  }, [businessId]);

  // LAZY: busca quando a aba monta.
  useEffect(load, [load]);

  // Approve/Reject do DemandRichCard transiciona no backend → refetch via
  // realtime mantém a lista coerente.
  useSupabaseRealtime({ table: 'demands' }, load);

  // DEM-2162 — donos distintos presentes na lista (genérico pra N colaboradores).
  // Só monta o seletor quando há MAIS de um dono (business colaborado); business
  // de dono único não mostra o filtro (nada a escolher).
  const owners = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of demands ?? []) {
      const ot = d.owner_tenant;
      if (ot?.id) map.set(ot.id, ot.name ?? `${ot.id.slice(0, 8)}…`);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [demands]);

  const hasMultipleOwners = owners.length > 1;

  // Aplica o filtro de dono client-side sobre a lista já carregada.
  const visibleDemands = useMemo(() => {
    const list = demands ?? [];
    if (ownerFilter === 'all') return list;
    if (ownerFilter === 'mine') {
      return list.filter((d) => d.owner_tenant?.id === myTenantId || d.tenant_id === myTenantId);
    }
    return list.filter((d) => d.owner_tenant?.id === ownerFilter || d.tenant_id === ownerFilter);
  }, [demands, ownerFilter, myTenantId]);

  return (
    <div className="wf-bizpanel">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 10,
          flexWrap: 'wrap',
        }}
      >
        <p style={{ fontSize: '.78rem', color: 'var(--wf-muted)', margin: 0 }}>
          Demandas deste negócio — reusa os cards do Dashboard
        </p>

        {/* DEM-2162 — seletor DINÂMICO de dono (só quando há >1 dono). */}
        {hasMultipleOwners && (
          <label
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.74rem' }}
            data-testid="demand-owner-filter"
          >
            <span style={{ color: 'var(--wf-muted)' }}>Dono:</span>
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              style={{
                fontSize: '.74rem',
                padding: '3px 8px',
                borderRadius: 6,
                border: '1px solid var(--wf-border, rgba(120,120,120,.25))',
                background: 'var(--wf-input-bg, transparent)',
                color: 'inherit',
              }}
            >
              <option value="all">Todas</option>
              {/* "Minhas" só aparece quando o tenant atual é um dos donos. */}
              {owners.some((o) => o.id === myTenantId) && (
                <option value="mine">Minhas</option>
              )}
              {/* Um item por dono distinto — genérico, nunca hardcoded. */}
              {owners
                .filter((o) => o.id !== myTenantId)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {`Do ${o.name}`}
                  </option>
                ))}
            </select>
          </label>
        )}
      </div>

      {error && <p className="text-xs mb-2" style={{ color: 'var(--wf-danger-text)' }}>{error}</p>}

      {demands === null ? (
        <div className="wf-repolist" aria-busy="true">
          {[1, 2, 3].map((i) => (
            <div key={i} className="wf-repocard" style={{ minHeight: 72 }} />
          ))}
        </div>
      ) : visibleDemands.length === 0 ? (
        <WfEmpty icon={<ClipboardList />}>
          Nenhuma demanda neste negócio — as demandas vinculadas a este negócio
          aparecem aqui.
        </WfEmpty>
      ) : (
        <div className="wf-repolist">
          {visibleDemands.map((d) => (
            <DemandRichCard
              key={d.id}
              demand={d}
              onClick={setDetailDemand}
              // DEM-2162 — badge de dono só quando o business tem >1 dono
              // (colaboração); caso contrário não polui o card.
              ownerLabel={hasMultipleOwners ? ownerLabelFor(d) : null}
              isOwnDemand={(d.owner_tenant?.id ?? d.tenant_id) === myTenantId}
            />
          ))}
        </div>
      )}

      <DemandDetailPanel demand={detailDemand} onClose={() => setDetailDemand(null)} />
    </div>
  );
}
